class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class QuantityInputDawn extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input-dawn', QuantityInputDawn);

class ProductVariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {

    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.clearMandatorySizeOnOtherOptionChange(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
          requestVariantId: this.getRequestVariantId(target.value),
          selectedValues: this.selectedValues,
        },
      });
    });
    requestAnimationFrame(() => {
      this.organiseSwatchesIntoGroups?.();
      this.syncSelectedGroupValue?.();
      this.updateColourGroupVisibility?.();
      this.reconcileSelectedOptions?.();
    });
  }

  updateColourGroupVisibility() {
    const fieldset = this.querySelector('.colour-group-container');
    if (!fieldset) return;

    const normalise = (raw) => {
      const t = (raw || '').toLowerCase().trim();
      if (t === 'save on' || t === 'discounted' || t === 'save') return 'save';
      if (t === 'new') return 'new';
      return 'regular';
    };

    const inputs = Array.from(fieldset.querySelectorAll('input[data-variant-type]'));

    const groups = { save: [], new: [], regular: [] };

    inputs.forEach(input => {
      const type = normalise(input.dataset.variantType);
      groups[type].push(input);
    });

    const groupHasEnabled = (groupInputs) =>
        groupInputs.some(input => !input.classList.contains('is-disabled') && !input.disabled);

    Object.entries(groups).forEach(([type, groupInputs]) => {
      const title = fieldset.querySelector(`.colour-group-title--${CSS.escape(type)}`);
      if (!title) return;

      title.hidden = !groupHasEnabled(groupInputs);
    });
  }

  syncSelectedGroupValue() {
    const fieldset = this.querySelector('.colour-group-container');
    if (!fieldset) return;

    const checked = fieldset.querySelector('input[type="radio"]:checked');
    if (!checked) return;

    const value = checked.value;
    const labelValue = checked.dataset.selectedValue;
    const typeRaw = checked.dataset.variantType || 'regular';
    const type = typeRaw.toLowerCase().trim();

    // Clear all group selected spans
    fieldset
        .querySelectorAll('.colour-group-title [data-selected-value]')
        .forEach(el => el.innerHTML = '');

    // Set correct group span
    const groupTitle = fieldset.querySelector(
        `.colour-group-title--${CSS.escape(type)}`
    );

    if (groupTitle) {
      const span = groupTitle.querySelector('[data-selected-value]');
      if (span) span.innerHTML = labelValue;
    }
  }

  organiseSwatchesIntoGroups() {
    const fieldset = this.querySelector('.colour-group-container');
    if (!fieldset) return;

    // Remove previously inserted titles
    //fieldset.querySelectorAll('.colour-group-title').forEach(el => el.remove());

    const normalise = (raw) => {
      const t = (raw || '').toLowerCase().trim();
      if (t === 'save on' || t === 'discounted' || t === 'save') return 'save';
      if (t === 'new') return 'new';
      return 'regular';
    };

    const inputs = Array.from(
        fieldset.querySelectorAll('input[data-variant-type]')
    );

    const saveItems = [];
    const newItems = [];
    const regItems = [];

    inputs.forEach(input => {
      const type = normalise(input.dataset.variantType);
      const label = fieldset.querySelector(
          `label[for="${CSS.escape(input.id)}"]`
      );

      if (!label) return;

      if (type === 'save') saveItems.push([input, label]);
      if (type === 'new') newItems.push([input, label]);
      if (type === 'regular') regItems.push([input, label]);
    });

    const insertTitle = (text, type) => {
      // add <span data-selected-value></span> inside this title
      const span = document.createElement('span');
      span.className = `colour-group-title colour-group-title--${type}`;
      span.textContent = text+":";

      const selectedValueSpan = document.createElement('span');
        selectedValueSpan.setAttribute('data-selected-value', '');
        selectedValueSpan.className = 'selected-value';
        span.appendChild(selectedValueSpan);

      fieldset.appendChild(span);
    };

    // Append Save first
    if (saveItems.length) {
      insertTitle('Save on selected colours', 'save');
      saveItems.forEach(([input, label]) => {
        fieldset.appendChild(input);
        fieldset.appendChild(label);
      });
    }

    // Then New
    if (newItems.length) {
      insertTitle('New Colours', 'new');
      newItems.forEach(([input, label]) => {
        fieldset.appendChild(input);
        fieldset.appendChild(label);
      });
    }

    if (regItems.length && (saveItems.length || newItems.length)) {
      insertTitle('More Colours', 'regular');


      regItems.forEach(([input, label]) => {
        fieldset.appendChild(input);
        fieldset.appendChild(label);
      });
    }

    // Remove original legend selected value span
    const legend = fieldset.querySelector('legend.form__label');
    if (legend && (saveItems.length || newItems.length)) {
      const originalSelected = legend.querySelector('[data-selected-value]');
      if (originalSelected) {
        legend.remove(); // completely remove from DOM
      }
    }
    this.updateColourGroupVisibility?.();
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;
    const labelValue = target.dataset.selectedValue;

    if (tagName === 'SELECT' && target.selectedOptions.length) {

      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');

      // change previous target.selectedOptions with new value
      const newSelectedOption = Array.from(target.options).find((option) => option.value === value);
      if (newSelectedOption) {
        newSelectedOption.setAttribute('selected', 'selected');
      }

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .product-option__swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('product-option__swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('product-option__swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {

      const fieldset = target.closest('.colour-group-container');
      if (!fieldset) {
        // Size (and other non-colour options): update the legend straight away instead of waiting
        // for the section re-render.
        const selectedValue = target
          .closest('.product-form__input')
          ?.querySelector(':scope > legend [data-selected-value]');
        if (selectedValue) selectedValue.textContent = labelValue || value;
        return;
      }

      const typeRaw = target.dataset.variantType || 'regular';
      const type = typeRaw.toLowerCase().trim();

      // Find matching group title
      const groupTitle = fieldset.querySelector(
          `.colour-group-title--${CSS.escape(type)}`
      );

      // Clear all group selected values first
      fieldset.querySelectorAll('.colour-group-title [data-selected-value]')
          .forEach(el => el.innerHTML = '');

      // Update correct group
      if (groupTitle) {
        const selectedSpan = groupTitle.querySelector('[data-selected-value]');
        if (selectedSpan) selectedSpan.innerHTML = labelValue;
      }
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  /**
   * When a non-size option (e.g. colour) changes, keep the picked size if the new selection still
   * makes a variant with it (e.g. Chinese Blue 10 -> Geranium 10). Otherwise drop it so it is not
   * sent in the option_values request: the server would resolve "new colour + old size" and the
   * button would reflect that size's stock even though no size is shown as selected.
   */
  clearMandatorySizeOnOtherOptionChange(eventTarget) {
    const group = this.querySelector('[data-mandatory-size-select="true"]');
    if (!group || group.contains(eventTarget)) return;

    // data-all-variants leaves out ghost SKUs, so a ghost size counts as not available.
    let variants = [];
    try {
      variants = JSON.parse(this.querySelector('[data-all-variants]')?.textContent || '[]');
    } catch (e) {}
    if (!Array.isArray(variants)) variants = [];
    variants = variants.filter((v) => Array.isArray(v.options));

    const selected = this.selectedValues.filter(Boolean);
    const input = this.getInputForEventTarget(eventTarget);
    // Over 250 variants the list only has the first 250 plus the current colour's, so the colour
    // just picked may not be in it. Its data-variant-id is that colour in the current size, blank
    // when the colour doesn't come in that size.
    const sizeStillExists = variants.some((v) => v.options.includes(input?.value))
      ? variants.some((v) => selected.every((value) => v.options.includes(value)))
      : Boolean(input?.dataset?.variantId);
    if (this.getSelectedMandatorySize() && sizeStillExists) return;

    group.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.checked = false;
    });

    const select = group.querySelector('select');
    if (select) {
      Array.from(select.options).forEach((o) => o.removeAttribute('selected'));
      const placeholder = Array.from(select.options).find((o) => o.value === '');
      if (placeholder) placeholder.setAttribute('selected', 'selected');
      select.value = '';
    }
  }

  /** The value picked in the mandatory size group, or null when there's no group or no pick. */
  getSelectedMandatorySize() {
    const group = this.querySelector('[data-mandatory-size-select="true"]');
    const radio = group?.querySelector('input[type="radio"]:checked');
    if (radio) return radio.value;
    return group?.querySelector('select')?.value || null;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'))
      .map(({ dataset }) => dataset.optionValueId)
      .filter(Boolean);
  }
  /**
   * The re-render request is sent with the selected option value ids, unless they don't make a real
   * variant (the server would return null and render "Unavailable"). Then this returns a variant id
   * to request instead:
   *
   * - Mandatory size select, no size picked: a variant of the selected colour (in stock if
   *   possible). This only affects the request: the size stays unselected on screen and the form's
   *   variant id is cleared afterwards (see ProductInfo.isMandatorySizePending).
   * - Otherwise, if the new selection isn't a variant (e.g. Light Denim 8S -> Chinese Blue, sold
   *   in 6-20), the closest one that keeps the value just picked.
   */
  getRequestVariantId(changedValue) {
    let variants;
    try {
      variants = JSON.parse(this.querySelector('[data-all-variants]')?.textContent || '[]');
    } catch (e) {
      return null;
    }
    if (!Array.isArray(variants)) return null;
    variants = variants.filter((v) => Array.isArray(v.options));

    return (
      this.getMandatorySizeCandidate(variants)?.id ??
      this.getClosestVariantId(variants, changedValue)
    );
  }

  /**
   * Null when the selected values already make a variant. Otherwise the variant with the value just
   * picked that keeps the most of the other selected values, preferring in stock ones.
   */
  getClosestVariantId(variants, changedValue) {
    const selected = this.selectedValues.filter(Boolean);
    if (!changedValue || !selected.length) return null;
    if (variants.some((v) => selected.every((value) => v.options.includes(value)))) return null;

    const kept = (v) => selected.filter((value) => v.options.includes(value)).length;
    const best = variants
      .filter((v) => v.options.includes(changedValue))
      .reduce((best, v) => {
        if (!best) return v;
        const diff = kept(v) - kept(best);
        return diff > 0 || (diff === 0 && v.available && !best.available) ? v : best;
      }, null);

    return best ? best.id : null;
  }

  /**
   * With mandatory size select and no size picked: the variant (in stock if possible) that matches
   * the other selected options. Null when it doesn't apply.
   */
  getMandatorySizeCandidate(variants) {
    const group = this.querySelector('[data-mandatory-size-select="true"]');
    if (!group) return null;

    const sizeInputs = Array.from(group.querySelectorAll('[data-option-value-id]'));
    const isSizePicked = sizeInputs.some((el) => el.checked || el.selected && el.value);
    if (isSizePicked || !sizeInputs.length) return null;

    const otherValues = this.selectedValues.filter(
      (value) => value && !sizeInputs.some((el) => el.value === value)
    );
    const matching = variants.filter((v) => otherValues.every((value) => v.options.includes(value)));
    return matching.find((v) => v.available) || matching[0] || null;
  }

  get selectedValues() {
    return Array.from(
        this.querySelectorAll('select option[selected], fieldset input:checked')
    ).map(el => el.value);
  }

  /**
   * Ghost sizes are hidden server-side (no radio is rendered for them). So after a re-render
   * (initial load, colour change, etc.) if the variant the server kept selected is a ghost size,
   * NOTHING is checked in that option group. When that happens, auto-select the first available
   * (in-stock, non-ghost) size - falling back to the first visible size - and fire a change so the
   * correct variant loads.
   *
   * An out-of-stock but ACTIVE size is still rendered (struck through) and stays checked, so this
   * leaves it alone - matching "keep the previously selected size if it's active, even if OOS".
   * The colour group is never reconciled.
   */
  reconcileSelectedOptions() {
    const colourGroup = this.querySelector('.colour-group-container');
    const groups = Array.from(this.querySelectorAll('.product-form__input'));

    groups.forEach((group) => {
      if (colourGroup && group === colourGroup) return;
      if (group.dataset.mandatorySizeSelect === 'true') return;

      const radios = Array.from(group.querySelectorAll('input[type="radio"]'));
      if (!radios.length) return;

      // A rendered (non-ghost) size is selected -> nothing to do.
      if (radios.some((r) => r.checked)) return;

      // Otherwise the kept size was a ghost (not rendered): pick the first available size, then
      // fall back to the first visible size.
      const next =
        radios.find((r) => !r.disabled && !r.classList.contains('visually-disabled')) || radios[0];

      if (next) {
        radios.forEach((r) => {
          r.checked = false;
        });
        next.checked = true;
        next.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
}
customElements.define('product-variant-selects', ProductVariantSelects);

if (!customElements.get('product-info')) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
        this.productMediaGallery = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (this.productMediaGallery) {
          this.overRideMode = this.productMediaGallery.dataset.variantMediaMode == 'true';
        } else{
            this.overRideMode = false;
        }
        this.productVariantSelects = this.querySelector('product-variant-selects');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
        if (!this.overRideMode) {
          this.initUpdateMedia();
        }


        if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true' && !this.overRideMode) {
          this.updateVariantMedia();
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(
          ".product-form__quantity"
        );
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      initUpdateMedia() {
        const variantIdFromUrl = parseInt(window.location.search.split('?').filter(item => item.includes('variant='))[0]?.split('=')[1]) || null;
        if (!variantIdFromUrl) { return; }
        const variant = this.getSelectedVariant(this);
        if (!variant || !variantIdFromUrl) { return; }
        if (variant?.id === variantIdFromUrl) {
          this.updateMedia(variant?.featured_media?.id);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push(html =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach(element =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.preProcessHtmlCallbacks.push(html => {
          html.querySelectorAll('product-variant-selects').forEach(wrapper => {
            const hasSpecial = wrapper.querySelector(
                'input[data-variant-type="new"], input[data-variant-type="save"], input[data-variant-type="save on"], input[data-variant-type="discounted"]'
            );

            if (!hasSpecial) return;

            wrapper
                .querySelectorAll('.colour-group-container > legend.form__label')
                .forEach(legend => legend.remove());
          });
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues, requestVariantId }
      }) {

        if (!this.contains(event.target)) return;

        // Ensure Bloomreach/Exponea "Notify me" weblayer sees the correct stock state
        // immediately (it reads `dataLayer` after a 1s timeout on label click).
        try {
          const allVariantsEl = this.querySelector('product-variant-selects [data-all-variants]');
          const variants = allVariantsEl ? JSON.parse(allVariantsEl.textContent) : null;

          // Read selected option values directly from DOM (more reliable than getters here).
          const variantSelectsEl = this.querySelector('product-variant-selects');
          const selectedValues = [];
          if (variantSelectsEl) {
            variantSelectsEl.querySelectorAll('fieldset').forEach((fs) => {
              const checked = fs.querySelector('input[type="radio"]:checked');
              if (checked) selectedValues.push(checked.value);
            });
            variantSelectsEl.querySelectorAll('select').forEach((sel) => {
              if (sel.value) selectedValues.push(sel.value);
            });
          }
          
          const matched =
            Array.isArray(variants) && selectedValues.length
              ? variants.find(v => Array.isArray(v.options) && v.options.join('||') === selectedValues.join('||'))
              : null;
          
          if (matched && typeof matched.available === 'boolean') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'view_item',
              view_item_data: {
                product_id: this.dataset?.productId ? String(this.dataset.productId) : '',
                variant_id: matched.id ? String(matched.id) : '',
                sku: matched.sku ? String(matched.sku) : '',
                out_of_stock: !matched.available,
              },
            });
          }
        } catch (e) {
          // ignore
        }

        this.resetProductFormState();

        const productUrl = (
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url
        ).split("?")[0];

        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
            // The variant id comes from this product's variants, so it's no use when swapping product.
            shouldSwapProduct ? null : requestVariantId
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(
                productUrl,
                // the size the request was made with, to re-check after the re-render
                this.querySelector('product-variant-selects')?.getSelectedMandatorySize?.()
              )
        });
      }

      /**
       * The mandatory-size fieldset is never re-checked by the server (it's always suppressed so
       * nothing is preselected). That means every AJAX re-render - including the one triggered by
       * the size click itself - comes back with the size unchecked, wiping out whatever the visitor
       * just picked. Restore the size the request was made with: the one just clicked, or the one
       * kept across a colour change because the new colour has it too. When a colour change cleared
       * the size (the new colour doesn't have it), there's nothing to restore, forcing a fresh pick.
       */
      restoreMandatorySizeSelection(sizeValue) {
        if (!sizeValue) return;

        const newGroup = this.querySelector('[data-mandatory-size-select="true"]');
        if (!newGroup) return;

        const radio = Array.from(
          newGroup.querySelectorAll('input[type="radio"]')
        ).find((r) => r.value === sizeValue);
        if (radio) {
          radio.checked = true;
          // The server renders the legend label empty too, so put the size name back.
          const selectedValue = newGroup.querySelector(':scope > legend [data-selected-value]');
          if (selectedValue) selectedValue.textContent = radio.dataset.selectedValue || radio.value;
          return;
        }

        const select = newGroup.querySelector('select');
        const option = select
          ? Array.from(select.options).find((o) => o.value === sizeValue)
          : null;
        if (select && option) {
          Array.from(select.options).forEach((o) => o.removeAttribute('selected'));
          option.setAttribute('selected', 'selected');
          select.value = option.value;
        }
      }

      isMandatorySizePending() {
        const group = this.querySelector('[data-mandatory-size-select="true"]');
        if (!group) return false;
        if (group.querySelector('input[type="radio"]:checked')) return false;
        const select = group.querySelector('select');
        if (select && select.value) return false;
        return true;
      }

      /**
       * True if any variant matching the currently selected non-size options (e.g. colour) is in
       * stock. Used to decide the button state while no size is selected.
       */
      hasAvailableVariantForSelection() {
        const variantSelects = this.querySelector('product-variant-selects');
        const allVariantsEl = variantSelects?.querySelector('[data-all-variants]');
        if (!allVariantsEl) return true;

        let variants;
        try {
          variants = JSON.parse(allVariantsEl.textContent);
        } catch (e) {
          return true;
        }
        if (!Array.isArray(variants)) return true;

        const selectedValues = [];
        variantSelects.querySelectorAll('fieldset input[type="radio"]:checked, select').forEach((el) => {
          if (el.closest('[data-mandatory-size-select="true"]')) return;
          if (el.value) selectedValues.push(el.value);
        });

        return variants.some(
          (v) =>
            v.available &&
            Array.isArray(v.options) &&
            selectedValues.every((value) => v.options.includes(value))
        );
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return html => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(
            html.querySelector(selector)
          );
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          if (variant) {
            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant
              }
            });
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then(response => response.text())
          .then(responseText => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value

            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch(error => {
            if (error.name === "AbortError") {
              // console.log("Fetch aborted by user");
            } else {
              // console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "product-variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getSelectedColour() {
        const selected = this.querySelector(
            'product-variant-selects fieldset input:checked'
        );
        return selected?.value || null;
      }

      getVariantMediaMap() {
        const el = document.getElementById(`VariantMediaMap-${this.dataset.productId}`);
        return el ? JSON.parse(el.textContent) : {};
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false,
        variantId = null
      ) {
        const params = [];

        !shouldFetchFullPage &&
          params.push(`section_id=${this.sectionId}`);

        if (variantId) {
          params.push(`variant=${variantId}`);
        } else if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const productVariantSelects = html.querySelector("product-variant-selects");
        if (productVariantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            productVariantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      getNextAvaliableId() {
        const selectedColour = this.getSelectedColour();
        if (!selectedColour) return null;

        const map = this.getVariantMediaMap();
        const colourData = map[selectedColour];

        if (!colourData || !colourData.variant_id) return null;

        return colourData.variant_id;
      }

      updateMediaForProduct(variantId) {
        if (!variantId) return;

        const url = `${this.dataset.url}?variant=${variantId}&section_id=${this.sectionId}`;

        fetch(url)
            .then(response => response.text())
            .then(responseText => {
              const html = new DOMParser().parseFromString(responseText, "text/html");

              const newMedia = html.querySelector(
                  `[id^="MediaGallery-"]`
              );

              const currentMedia = this.querySelector(
                  `[id^="MediaGallery-"]`
              );

              if (!newMedia || !currentMedia) return;

              HTMLUpdateUtility.viewTransition(
                  currentMedia,
                  newMedia,
                  this.preProcessHtmlCallbacks,
                  this.postProcessHtmlCallbacks
              );
            })
            .catch(err => console.error("Media fetch error:", err));
      }

      handleUpdateProductInfo(productUrl, mandatorySizeValue) {
        return html => {
          const variant = this.getSelectedVariant(html);

          // this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.restoreMandatorySizeSelection(mandatorySizeValue);
          const sizePending = this.isMandatorySizePending();
          this.updateURL(productUrl, variant?.id);
          // No size picked yet: the server still resolves a full variant (e.g. the first size),
          // but it must not become the form's variant.
          this.updateVariantInputs(sizePending ? null : variant?.id);
          this.updateVariantDiscountBadge(variant?.price, variant?.compare_at_price);
          this.updateVariantDetails(variant?.id);
          // Inserted logic for pickupAvailability after updateVariantDetails
          if (this.pickupAvailability && variant?.id) {
            this.pickupAvailability.dataset.variantId = variant.id;
            this.pickupAvailability.fetchAvailability(variant.id);
          }

          if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true' && !this.overRideMode) {
            this.updateVariantMedia(html);
          }
          if (!variant) {
            this.setUnavailable();
            if (this.overRideMode){
              this.updateMediaForProduct(this.getNextAvaliableId());
            }
            return;
          }
          if (!this.overRideMode){
          this.updateMedia(variant?.featured_media?.id);
          }


          const updateSourceFromDestination = (
            id,
            shouldHide = source => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {

              destination.innerHTML = source.innerHTML;

              if (id === 'Inventory') {
                const inventoryThreshold = parseInt(source.dataset.inventoryThreshold);
                const inventoryQuantity = parseInt(source.dataset.inventoryQuantity);
                destination.classList.toggle("hidden", inventoryThreshold && inventoryQuantity > inventoryThreshold || inventoryQuantity < 1);
              } else {
                destination.classList.toggle("hidden", shouldHide(source));
              }
            }
          };

          updateSourceFromDestination("ProductPurchaseInfo");
          updateSourceFromDestination("ProductStickyCart");
          updateSourceFromDestination("ViewItemVariantMap");


          updateSourceFromDestination("price");
          updateSourceFromDestination("MediaGallery");
          updateSourceFromDestination("badge");
          updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));

          updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");


          updateSourceFromDestination("PreorderInfo");
          window.initPreorderCountdowns?.();

          const fetchedButton =
              html.getElementById(`ProductSubmitButton-${this.sectionId}`);

          const isDisabledByVariant =
              fetchedButton?.hasAttribute("disabled") ?? true;

          // Sync preorder attribute from fetched HTML before toggling button text
          const fetchedForm = html.querySelector('product-form');
          if (fetchedForm && this.productForm) {
            if (fetchedForm.hasAttribute('data-has-pre-order')) {
              this.productForm.setAttribute('data-has-pre-order', '');
            } else {
              this.productForm.removeAttribute('data-has-pre-order');
            }
          }

          const isDisabledByCountry =
              this.getAttribute("data-disable-for-user");
          if (isDisabledByCountry === 'true'   ) {
            this.productForm?.toggleSubmitButton(
                true,
                window.variantStrings.unavailable
            );
          } else if (sizePending) {
            // Button state reflects the colour as a whole, not the variant the server fell back to.
            // Clicking it with no size shows the "please select a size" message.
            const colourAvailable = this.hasAvailableVariantForSelection();
            this.productForm?.toggleSubmitButton(
                !colourAvailable,
                colourAvailable ? undefined : window.variantStrings.soldOut
            );
          } else {
            this.productForm?.toggleSubmitButton(
                isDisabledByVariant,
                isDisabledByVariant
                    ? window.variantStrings.soldOut
                    : undefined
            );
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant
            }
          });
        };
      }

      updateVariantMedia() {
        const select_variant_options_handle = this.querySelector(
          'product-variant-selects [data-selected-variant-options-handle]'
        )?.innerHTML.trim();

        if (!select_variant_options_handle) {
          const productAllVariants = this.productVariantSelects.querySelector('[data-all-variants]').innerHTML.trim();

          // Make sure to attach the event listener to the correct element
          this.productVariantSelects.addEventListener('click', (event) => {
            const closestVariantOptionInput = event.target.closest('.product-option__input');

            // Ensure that you are finding the correct input
            if (closestVariantOptionInput) {
              const closestVariantOptionInputValue = closestVariantOptionInput.value;

              // Parse productAllVariants and check for the selected variant
              const availableVariant = JSON.parse(productAllVariants).find(variant => {
                return variant.options.some(option => option === closestVariantOptionInputValue);
              });
            } else {
              // console.log('No variant option input found');
            }
          });
        }

        this.productMediaGallery.querySelectorAll('[data-media-group]').forEach(element => element.classList.add('hidden'));

        this.productMediaGallery.querySelectorAll(`[data-media-group="${select_variant_options_handle}"]`).forEach(element => element.classList.remove('hidden'));

        this.productMediaGallery.querySelectorAll('swiper-product-gallery').forEach(swiperSlide => {
          if (swiperSlide.instance) {
            swiperSlide.instance.update(); // Update the Swiper instance
          } else {
            swiperSlide.init(); // If the instance is not available, reinitialize
          }
        });
      }

      updateVariantOptionMedia() {
        this.productMediaGallery.querySelectorAll('[data-media-group]:not(.hidden)').forEach(element => {

        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach(productForm => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${
            variantId ? `?variant=${variantId}` : ""
          }`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      updateSellingPlanURL(sellingPlanId, selectedVariantId) {
        if (!selectedVariantId) return;
        if (!sellingPlanId) {
          window.history.replaceState({}, "", `${window.location.href.replace(/&selling_plan=\d+/, "")}`);
        } else {
          const params = new URLSearchParams(window.location.search);
          params[params.has("variant") ? "set" : "append"](
            "variant",
            selectedVariantId
          );
          if (sellingPlanId) {
            params[params.has("selling_plan") ? "set" : "append"](
              "selling_plan",
              sellingPlanId
            );
          } else {
            params.delete("selling_plan");
          }
          window.history.replaceState({}, "", `${this.dataset.url}?${params.toString()}`);
        }
      }

      updateVariantDetails(variantId) {
        const productVariantDetails = this.querySelectorAll('.product__metafield_for_variant-block');
        if (productVariantDetails) {
          productVariantDetails.forEach((productVariantDetail) => {
            const block_id = productVariantDetail.getAttribute('data-block-id');
            const metafieldDataElement = this.querySelector(`#product__metafield_for_variant-block--${block_id}`);
            if (!metafieldDataElement) {
              return;
            }
            try {
              let productVariantDetailsData = JSON.parse(metafieldDataElement?.textContent);
              productVariantDetail.innerHTML = productVariantDetailsData?.[variantId]?.replaceAll('\n', '<br />') || '';
            } catch (error) {
              console.error('Error parsing JSON', error);
            }
          });
        }
      }

      updateVariantDiscountBadge(price, compare_at_price) {
        const discountBadge = this.querySelector(".product-card__badge--discount");
        if (!discountBadge) return;


        if (compare_at_price > price) {
          discountBadge.classList.remove("hidden");
          const percentageElement = discountBadge.querySelector(".percentage");
          // calculate discount percentage
          const discountPercentage = Math.round(
            ((compare_at_price - price) / compare_at_price) * 100
          );
          // update discount percentage
          percentageElement.textContent = discountPercentage;
        } else {
          discountBadge.classList.add("hidden");
        }
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Quantity-Rules"
        ]
          .map(id => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        // if layout is grid, add .active class to the relevant swiper-slide
        const gridLayout = this.querySelector('.main-product__media--grid');
        if (gridLayout) {
          // remove active class from all swiper-slides
          gridLayout.querySelectorAll('.swiper-slide').forEach(item => {
            item.classList.remove('active');
          });
          // add .active class to the swiper-slide with the matching data-media-id
          const activeSlide = gridLayout.querySelector(`.swiper-slide[data-media-id="${variantFeaturedMediaId}"]`);
          if (activeSlide) {
            activeSlide.classList.add('active');
          }
        }

        if (
          this.querySelector(".main-product__media")?.hasAttribute(
            "data-desktop-grid"
          ) && window.innerWidth >= 750
        ) {
          this.scrollToImage(variantFeaturedMediaId);
        }

        const productMedia = this.querySelector(
          "swiper-product-gallery"
        );
        productMedia?.setActiveMedia(variantFeaturedMediaId);
      }

      scrollToImage(variantFeaturedMediaId) {
        this.querySelectorAll("[data-media-id]").forEach(img => {
          if (
            img.getAttribute("data-media-id") ==
            variantFeaturedMediaId
          ) {
            img.scrollIntoView({
              block: "end",
              behavior: "smooth"
            });
          }
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step
            ? parseInt(this.quantityInput.step)
            : 1
        };

        let min = data.min;
        const max =
          data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min)
          min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then(response => response.text())
          .then(responseText => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch(e => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label"
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step"
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.dataset.section}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("product-variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener(
          "submit",
          this.onSubmitHandler.bind(this)
        );
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";

        this.initMandatorySizeSelect();
      }

      /**
       * The size fieldset lives inside <product-variant-selects>, which is entirely replaced
       * (fresh DOM node) on every AJAX option-change re-render - so a cached element reference
       * goes stale the moment the visitor touches any option. Everything here re-resolves the
       * group by selector on demand, and the "clear the error" listener is delegated to the
       * document so it keeps working across swaps instead of being bound to a node that gets
       * thrown away.
       */
      initMandatorySizeSelect() {
        this.mandatorySizeGroupSelector = `#product-variant-selects-${this.dataset.sectionId} [data-mandatory-size-select="true"]`;
        this.mandatorySizeErrorId = `SizeSelectError-${this.dataset.sectionId}`;

        document.addEventListener("change", (event) => {
          if (!event.target.closest?.(this.mandatorySizeGroupSelector)) return;
          if (this.hasSizeSelected()) this.hideMandatorySizeError();
        });
      }

      get mandatorySizeGroup() {
        return document.querySelector(this.mandatorySizeGroupSelector);
      }

      hasSizeSelected() {
        const group = this.mandatorySizeGroup;
        if (!group) return true;
        if (group.querySelector('input[type="radio"]:checked')) return true;
        const select = group.querySelector("select");
        if (select && select.value) return true;
        return false;
      }

      showMandatorySizeError() {
        const error = document.getElementById(this.mandatorySizeErrorId);
        if (!error) return;
        error.hidden = false;
        this.mandatorySizeGroup?.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }

      hideMandatorySizeError() {
        const error = document.getElementById(this.mandatorySizeErrorId);
        if (!error) return;
        error.hidden = true;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (
          this.submitButton.getAttribute("aria-disabled") === "true"
        )
          return;

        if (!this.hasSizeSelected()) {
          this.showMandatorySizeError();
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove(
          "hidden"
        );

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart
              .getSectionsToRender()
              .map(section => section.section)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        formData.append(
          "properties[__is_markdown]",
          window.getIsMarkdownForVariant?.(formData.get("id"), this) ? "true" : "false"
        );

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(response => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector(
                ".sold-out-message"
              );
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response
              });
            this.error = false;
            this.cart.renderContents(response);
          })
          .catch(e => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error)
              this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add(
              "hidden"
            );
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute(
          "hidden",
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = this.hasAttribute(
            "data-has-selling-plan"
          )
            ? window.variantStrings.addSubscriptionToCart
            : this.submitButtonText.textContent = this.hasAttribute(
                "data-has-pre-order"
              )
            ?  window.variantStrings.preOrder
            : window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}

document.querySelectorAll('.product__content > *').forEach((el) => {
  const hasImage = el.querySelector('img');
  const hasVideo = el.querySelector('video');
  const hasIframe = el.querySelector('iframe');
  const hasText = el.textContent.trim().length > 0;

  if (!hasText && !hasImage && !hasVideo && !hasIframe) {
    el.remove();
  }
});